'use client';
import { Button, Integrations, TooltipContent, TooltipTolq, Typography } from 'components';
import Badge from 'components/Badge/Badge';
import SelectItemInput from 'components/Inputs/SelectItemInput';
import NewComments from 'components/NewComments';

import { THEME_TASK_STATUS, THEME_TRANSLATION_REQUEST_STATUS } from 'constants/status';
import {
  BulkDownloadFormatEnum,
  IntegrationEnum,
  Maybe,
  Task,
  TaskStatusEnum,
  TranslationRequestStatusEnum
} from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import useDownloadBulkFile from 'hooks/useDownloadBulkFile';
import {
  DownloadIcon,
  EditIcon,
  GlobeAiIcon,
  InfoIcon,
  SettingsIcon,
  TranslationIcon,
  TrashIcon,
  WarningIcon
} from 'lib/svgs/Icons';
import { useLocale, useTranslations } from 'next-intl';
import { useTranslationRequestDestroy } from 'providers/TranslationRequests/useTranslationRequestDestroy';
import { FC, HTMLAttributes, memo, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { KeysEnum } from 'types/KeysEnum';
import { cn } from 'utils/cn';
import { dateShort } from 'utils/time/dateShort';
import { useModalTranslationRequestContext } from '../_context/ModalTranslationRequestContext';

interface IRequestCardProps
  extends Omit<
    PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
    'onSelect' | 'value' | 'onChange'
  > {
  title: string;
  integration?: Maybe<IntegrationEnum>;
  date: string;
  status: TranslationRequestStatusEnum;
  languagePair: string;
  typeTranslation: string;
  duplicateOf?: string;
  totalWords: number;
  ordeableOrders: number;
  taskNumber: number;
  totalPrice: string;
  poCode: string;
  savings: string;
  isSelectItem?: boolean;
  isDuplicated?: boolean;
  hasAgent?: boolean;
  isQuote: boolean;
  pully: boolean;
  uploaded: boolean;
  code?: string;
  version: string;
  taskId?: string;
  duplicationSavings?: string;
  orderAcceptedDate?: string;
  preferredDeliveryDate?: string;
  comments?: number;
  commentsNew?: number;
  relatedTasks?: Maybe<Task[]>;
  onClick?: () => void;
  onEdit?: () => void;
  refetch: () => void;
}

const RequestCard: FC<IRequestCardProps> = ({
  title,
  version,
  taskId,
  code,
  pully,
  uploaded,
  date,
  status,
  languagePair,
  typeTranslation,
  totalWords,
  ordeableOrders,
  taskNumber,
  isDuplicated,
  totalPrice,
  poCode,
  savings,
  isSelectItem,
  isQuote,
  hasAgent,
  duplicationSavings,
  orderAcceptedDate,
  preferredDeliveryDate,
  comments,
  commentsNew,
  relatedTasks,
  duplicateOf,
  integration,
  onClick,
  onEdit,
  refetch
}) => {
  const locale = useLocale();
  const t = useTranslations();

  const { trVersions, setTrVersions } = useModalTranslationRequestContext();
  const { mutateAsync: translationRequestDestroy } = useTranslationRequestDestroy();

  const { loading, onDownload } = useDownloadBulkFile({
    versions: [version]
  });

  const [deletingQuote, setDeletingQuote] = useState(false);

  const isPending = status === TranslationRequestStatusEnum.Pending;
  const isFinished = status === TranslationRequestStatusEnum.Finished;
  const themeStatus = THEME_TRANSLATION_REQUEST_STATUS[status as TranslationRequestStatusEnum];

  const isChecked = useMemo(() => {
    if (!code) return false;

    return (trVersions as string[])?.includes(code);
  }, [code, trVersions]);

  const handleTryDeleteQuote = () => setDeletingQuote(true);
  const handleCancelDeleteQuote = () => setDeletingQuote(false);

  const handleDownload = () => {
    if (uploaded) return onDownload(BulkDownloadFormatEnum.File)();

    if (pully) return onDownload(BulkDownloadFormatEnum.Pully)();

    return onDownload(BulkDownloadFormatEnum.Plain)();
  };

  const handleComments = () => {
    window.location.href = `/translation/show-task/${taskId}#${KeysEnum.Comments}`;
  };

  const handleDeleteQuote = useCallback(async () => {
    try {
      handleCancelDeleteQuote();
      const response = await translationRequestDestroy({
        translationRequestVersion: version as string
      });

      if (response.success) {
        refetch();
        toaster.success(t('messages.success.quote_delete'));
      }
    } catch (error) {
      notifyException(error);
      toaster.error(t('messages.error.quote_delete'));
    }
  }, [refetch, t, translationRequestDestroy, version]);

  return (
    <div
      className={cn(
        'w-full rounded-[5px] border-1 border-transparent bg-white p-4 shadow-custom ',
        'transition duration-200 ease-in-out',
        isChecked ? 'scale-[101%] !border-primary-green-100' : ''
      )}
    >
      <div className="flex flex-row justify-between">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row gap-2">
            {themeStatus.icon}
            <Typography variant="regular4" className="text-tertiary-100 ">
              {t(`sources.${status}` as any)}
            </Typography>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {isDuplicated && (
            <Badge
              icon={<WarningIcon className="scale-[0.85] !text-white" />}
              className="underline-none relative h-6 !bg-status-yellow !py-0"
              classNameTypography="text-white !text-xs !font-regular"
            >
              {t('ordering_form.duplicated_content')}
            </Badge>
          )}
          {isSelectItem && (
            <SelectItemInput value={trVersions} onChange={setTrVersions} code={code ?? ''} />
          )}
        </div>
      </div>
      <span className="my-1 flex items-start gap-2">
        <Typography variant="regular1" className="!my-0 !leading-6 text-tertiary-200">
          #{version}
        </Typography>
        <Typography variant="bold1" className="lead !leading-6 text-tertiary-100 ">
          {title}
        </Typography>
      </span>
      {isPending && (
        <Typography variant="regular4" className="text-tertiary-300  ">
          {t('translation.this_tr_processing')}
        </Typography>
      )}
      <div className="flex flex-row gap-4">
        <Typography variant="regular4" className="flex flex-row gap-1 text-tertiary-100 ">
          <TranslationIcon className="mt-1 h-4 w-4" />
          {languagePair}
        </Typography>
        <Typography variant="regular4" className="flex flex-row gap-1 text-tertiary-100 ">
          <SettingsIcon className="mt-1 h-4 w-4" />
          {typeTranslation}
        </Typography>
        {hasAgent && (
          <Typography variant="regular4" className="flex flex-row gap-1 text-secondary-yellow ">
            <GlobeAiIcon className="mt-1 scale-[0.95] !text-secondary-yellow" />
            {t('components.request_card.agent')}
          </Typography>
        )}
        <Integrations type={integration} />
        <Typography
          variant="regular5"
          className="tracking-tight text-tertiary-300 dark:text-tertiary-300"
        >
          {dateShort(date)}
        </Typography>
      </div>
      {!isPending && (
        <div className="pb-5">
          <div className="flex flex-row gap-4 pt-1">
            <div className="my-auto flex shrink-0 flex-row flex-nowrap gap-4">
              <Typography variant="regular4" className="text-tertiary-300  ">
                {t('components.request_card.total_words')}
              </Typography>
              <Typography variant="medium4" className=" text-tertiary-100 ">
                {new Intl.NumberFormat(locale, {
                  notation: 'standard'
                }).format(totalWords)}
              </Typography>
            </div>
            <div className="flex shrink-0 flex-row flex-nowrap gap-4">
              <Typography variant="regular4" className="text-tertiary-300  ">
                {t('components.request_card.orderable_words')}
              </Typography>
              <Typography variant="medium4" className=" text-tertiary-100 ">
                {new Intl.NumberFormat(locale, {
                  notation: 'standard'
                }).format(ordeableOrders)}
              </Typography>
            </div>
            <div className="flex flex-row gap-4">
              <Typography variant="regular4" className="text-tertiary-300  ">
                {t('components.request_card.tasks')}
              </Typography>
              <Typography variant="medium4" className=" text-tertiary-100 ">
                {taskNumber}
              </Typography>
            </div>
            {!isQuote && (
              <div className="flex flex-row gap-4">
                <Typography variant="regular4" className="text-tertiary-300  ">
                  {t('components.request_card.comments')}
                </Typography>
                <div className="flex flex-row gap-2">
                  <Typography variant="medium4" className=" text-tertiary-100 ">
                    {comments}
                  </Typography>
                  {!!commentsNew && taskId && (
                    <span className="cursor-pointer font-medium" onClick={handleComments}>
                      <NewComments count={commentsNew} />
                    </span>
                  )}
                </div>
              </div>
            )}
            {!!poCode && (
              <div className="flex flex-row flex-nowrap gap-4 ">
                <Typography variant="regular4" className="flex shrink-0 text-tertiary-300">
                  {t('components.request_card.po_code')}
                </Typography>
                <div className="flex flex-row gap-2">
                  <Typography variant="medium4" className=" text-tertiary-100 ">
                    {poCode}
                  </Typography>
                </div>
              </div>
            )}
          </div>
          {!isQuote && (
            <div className="flex flex-row gap-4 pt-1">
              <div className="my-auto flex flex-row gap-4">
                <Typography variant="regular4" className="text-tertiary-300  ">
                  {t('components.request_card.duplication_savings')}
                </Typography>
                <Typography variant="medium4" className=" text-tertiary-100 ">
                  {duplicationSavings}
                </Typography>
              </div>
              <div className="flex flex-row items-center gap-4">
                <Typography variant="regular4" className="text-tertiary-300  ">
                  {t('components.request_card.order_accept_date')}
                </Typography>
                <div className="flex gap-1">
                  <Typography variant="medium4" className=" text-tertiary-100 ">
                    {orderAcceptedDate
                      ? dateShort(orderAcceptedDate)
                      : t('translation.index.processing')}
                  </Typography>
                  <TooltipTolq
                    placement={'top'}
                    label={
                      <TooltipContent>
                        {t('translation.index.order_accept_date_explanation')}
                      </TooltipContent>
                    }
                  >
                    <InfoIcon className="mb-0.5 scale-75 cursor-pointer" />
                  </TooltipTolq>
                </div>
              </div>
              {preferredDeliveryDate && (
                <div className="flex flex-row items-center gap-4">
                  <Typography variant="regular4" className="text-tertiary-300  ">
                    {t('components.request_card.preferred_date')}
                  </Typography>
                  <div className="flex gap-1">
                    <Typography variant="medium4" className=" text-tertiary-100 ">
                      {dateShort(preferredDeliveryDate)}
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div
        className={cn(
          'flex flex-row justify-between',
          !isPending ? 'border-t-1 border-tertiary-600 pt-4' : ''
        )}
      >
        <div className="flex flex-row gap-4">
          {isQuote ? (
            <>
              <div className="flex flex-row gap-2">
                <Typography variant="regular4" className="text-tertiary-300 ">
                  {t('components.request_card.price')}
                </Typography>
                <Typography variant="medium4xl" className="text-primary-blue-100">
                  {totalPrice}
                </Typography>
              </div>
              <div className="flex flex-row gap-2">
                <Typography variant="regular4" className=" text-tertiary-300 ">
                  {t('components.request_card.savings')}
                </Typography>
                <Typography variant="medium2" className="text-tertiary-100 ">
                  {savings}
                </Typography>
              </div>
            </>
          ) : (
            <>
              {!!relatedTasks?.length && (
                <div className="flex flex-row items-center gap-2">
                  <Typography variant="regular4" className=" text-tertiary-300 ">
                    {t('components.request_card.related_tasks', {
                      count: relatedTasks?.length ?? 0
                    })}
                  </Typography>
                  {relatedTasks?.slice(0, 3)?.map((task) => {
                    const themeTask = THEME_TASK_STATUS[task.status as TaskStatusEnum];
                    return (
                      <Badge
                        key={task.id}
                        {...themeTask}
                        href={`/translation/show-task/${task.id}`}
                      >
                        # {task.id}
                      </Badge>
                    );
                  })}
                  {relatedTasks && relatedTasks?.length > 3 && (
                    <Typography variant="bold4" className=" text-tertiary-300 ">
                      + {relatedTasks?.length - 3}
                    </Typography>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {!isPending && (
          <span className="flex items-start">
            {isFinished && (
              <Button
                size="medium"
                variant="text"
                label={t('translation.index.download')}
                className="!m-0 w-64"
                endAdornment={<DownloadIcon className="mb-1 scale-125" />}
                onClick={handleDownload}
                loading={loading}
                classNameLoading="border-secondary-link border-t-transparent"
              />
            )}
            {isQuote && !duplicateOf && (
              <Button
                size="medium"
                variant="text"
                label={t('translation.index.edit')}
                className="!m-0"
                endAdornment={<EditIcon />}
                onClick={onEdit}
              />
            )}
            <div className="flex flex-1 flex-col gap-2">
              <Button
                color={commentsNew ? 'primary' : 'secondary'}
                size="medium"
                variant={isQuote || commentsNew ? 'outlined' : 'contained'}
                label={t(
                  `translation.index.${(() => {
                    if (commentsNew) return 'unread_comments';

                    if (isQuote) return 'order_api';

                    return 'show_translation';
                  })()}`
                )}
                onClick={onClick}
              />
              {isQuote && (
                <>
                  {!deletingQuote ? (
                    <Button
                      size="small"
                      variant="text"
                      className="scale-75"
                      label={t('translation.index.delete_quote')}
                      startAdornment={<TrashIcon />}
                      onClick={handleTryDeleteQuote}
                    />
                  ) : (
                    <Typography variant="light3" className="flex scale-75 items-center gap-4">
                      {t('translation.index.are_you_sure')}
                      <Button
                        size="small"
                        variant="text"
                        className="!p-0"
                        label={t('translation.index.yes')}
                        onClick={handleDeleteQuote}
                      />
                      <Button
                        size="small"
                        variant="text"
                        className="!p-0"
                        label={t('translation.index.no')}
                        onClick={handleCancelDeleteQuote}
                      />
                    </Typography>
                  )}
                </>
              )}
            </div>
          </span>
        )}
      </div>
    </div>
  );
};

export default memo(RequestCard);
